import { useEffect } from "react";
import { Check, ArrowRight } from "lucide-react";
import { MaskedLines, FadeUp } from "../components/Reveal";
import { FloatingBee, HexPattern, Wave } from "../components/Bee";
import ContactBanner from "../components/ContactBanner";
import { SITE, PROGRAMS, DAY_TIMELINE } from "../data/site";

export default function Programs() {
    useEffect(() => {
        document.title = `Programs — ${SITE.name}`;
    }, []);

    return (
        <>
            {/* ---------- HERO ---------- */}
            <section
                data-testid="programs-hero"
                className="relative overflow-hidden pb-12 pt-28 sm:pt-36 lg:pb-16"
            >
                <HexPattern className="pointer-events-none absolute -right-24 -top-10 h-[420px] w-[640px] rotate-3" />
                <FloatingBee className="right-[8%] top-40 hidden lg:block" />
                <div className="wrap relative grid items-end gap-10 lg:grid-cols-[1.2fr_1fr]">
                    <div>
                        <FadeUp>
                            <span className="eyebrow">Our Programs</span>
                        </FadeUp>
                        <h1 className="mt-6 font-display text-[clamp(2.6rem,6vw,4.8rem)] font-semibold leading-[1.05] tracking-tight">
                            <MaskedLines
                                lines={[
                                    "From first wobbles",
                                    <>
                                        to{" "}
                                        <span className="text-honey-warm">
                                            ready-to-fly
                                        </span>
                                    </>,
                                ]}
                            />
                        </h1>
                    </div>
                    <FadeUp delay={0.35}>
                        <div>
                            <p className="max-w-md text-lg leading-relaxed text-ink-muted">
                                Four cozy rooms, one gentle philosophy:
                                child-first learning that follows curiosity and
                                builds real skills through play.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-2.5">
                                {[
                                    SITE.ages,
                                    "Small class ratios",
                                    "Year-round care",
                                ].map((chip) => (
                                    <span
                                        key={chip}
                                        className="rounded-full border border-honey/40 bg-white px-4 py-2 text-sm font-medium text-honey-warm"
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* ---------- AGE GROUPS ---------- */}
            <section
                data-testid="age-programs-list"
                className="wrap pb-8 pt-4 lg:pb-12"
            >
                <div className="space-y-16 lg:space-y-24">
                    {PROGRAMS.map((p, i) => (
                        <FadeUp key={p.id}>
                            <article className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16">
                                <div
                                    className={`relative ${
                                        i % 2 ? "lg:order-2" : ""
                                    }`}
                                >
                                    <div className="overflow-hidden rounded-b-[2.5rem] rounded-t-[10rem] border-4 border-white shadow-[0_24px_60px_rgba(185,125,10,0.18)]">
                                        <img
                                            src={p.image}
                                            alt={`${p.name} — ${p.group} classroom`}
                                            loading="lazy"
                                            className="aspect-[5/4] w-full object-cover"
                                        />
                                    </div>
                                    <span className="absolute -top-4 left-6 rounded-full bg-honey px-4 py-2 font-display text-sm font-semibold text-ink shadow-md">
                                        {p.age}
                                    </span>
                                </div>
                                <div>
                                    <p className="eyebrow">
                                        {p.group} · Ratio {p.ratio}
                                    </p>
                                    <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                                        {p.name}
                                    </h2>
                                    <p className="mt-3 max-w-lg leading-relaxed text-ink-muted">
                                        {p.description}
                                    </p>
                                    <ul className="mt-5 space-y-2.5">
                                        {p.highlights.map((h) => (
                                            <li
                                                key={h}
                                                className="flex items-start gap-2.5 text-[15px] text-ink/85"
                                            >
                                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-honeysoft">
                                                    <Check className="h-3 w-3 text-honey-warm" />
                                                </span>
                                                {h}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-7 flex flex-wrap items-center gap-3">
                                        <a
                                            href={SITE.phoneHref}
                                            data-testid={`program-${p.id}-tuition-btn`}
                                            className="btn-ghost !py-2.5 !text-sm"
                                        >
                                            Ask about tuition
                                            <ArrowRight className="h-4 w-4" />
                                        </a>
                                        <span className="rounded-full border border-honey/30 bg-warm px-4 py-2.5 text-sm font-medium text-honey-warm">
                                            Full-day & half-day options
                                        </span>
                                    </div>
                                </div>
                            </article>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* ---------- A DAY AT BUSY BEES ---------- */}
            <section
                data-testid="a-day-at-busy-bees-timeline"
                className="relative"
            >
                <Wave fill="#FFF9E6" />
                <div className="-mt-px bg-warm py-10 lg:py-14">
                    <div className="wrap grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
                        <div className="self-start lg:sticky lg:top-28">
                            <FadeUp>
                                <span className="eyebrow">
                                    A Day at Busy Bees
                                </span>
                            </FadeUp>
                            <FadeUp delay={0.08}>
                                <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                                    Sunrise to pickup, happily spent.
                                </h2>
                            </FadeUp>
                            <FadeUp delay={0.15}>
                                <p className="mt-4 max-w-md leading-relaxed text-ink-muted">
                                    A steady rhythm gives little bees the
                                    confidence to explore. Here is how a
                                    typical day hums along.
                                </p>
                            </FadeUp>
                        </div>
                        <ol className="relative space-y-8 border-l-2 border-dashed border-honey/40 pl-8 sm:pl-10">
                            {DAY_TIMELINE.map((d, i) => (
                                <FadeUp key={d.time} delay={i * 0.03}>
                                    <li className="relative">
                                        <span
                                            className="absolute -left-[41px] top-1.5 sm:-left-[49px]"
                                            data-testid={`timeline-dot-${i}`}
                                        >
                                            <span className="block h-3.5 w-3.5 rounded-full bg-honey ring-4 ring-highlight" />
                                        </span>
                                        <p className="font-display text-lg font-semibold">
                                            {d.time}
                                        </p>
                                        <p className="font-semibold text-ink/85">
                                            {d.label}
                                        </p>
                                        <p className="text-sm text-ink-muted">
                                            {d.note}
                                        </p>
                                    </li>
                                </FadeUp>
                            ))}
                        </ol>
                    </div>
                </div>
                <Wave fill="#FFF9E6" className="rotate-180" />
            </section>

            {/* ---------- VISIT CALLOUT ---------- */}
            <ContactBanner
                testid="programs-visit-callout"
                title="Find the right hive for your bee"
                sub="Call or email to ask about availability, tuition and tour times — we will happily show you around."
            />
        </>
    );
}
